import { Select, MenuItem, Checkbox, ListItemText, Box } from "@mui/material";
import { ChevronDown } from "../../icons/ChevronDown";
import "../../../assets/css/CustomMultiSelect.css";
import { buildSVGIconSimilarAssetsSVGIcon } from "@/components/common/maps/AssetMapHelperFunctions";

const PIN_IMG_WIDTH = 20;
const PIN_IMG_HEIGHT = 31;

function getSimilarAssetsPinIconUrl(pinIndex: number): string | undefined {
  if (
    typeof window === "undefined" ||
    !window.google?.maps?.Size ||
    !Number.isFinite(pinIndex)
  ) {
    return undefined;
  }
  try {
    const icon = buildSVGIconSimilarAssetsSVGIcon(pinIndex) as google.maps.Icon;
    return typeof icon?.url === "string" ? icon.url : undefined;
  } catch {
    return undefined;
  }
}

interface Option {
  id: string | number;
  [key: string]: any;
}

interface CustomMultiSelectProps {
  value: (string | number)[];
  onChange: (event: any) => void;
  options: Option[];
  labelKey: string;
  placeholder?: string;
  minWidth?: string;
  heightOverride?: string;
  fontSizeOverride?: string;
  redOutline?: boolean;
  fullWidth?: boolean;
  useSVGIcons?: boolean;
  /** When set, pin color uses `Number(option[pinIndexKey])` (e.g. comp sources with string `id`). */
  pinIndexKey?: string;
  dropDownWidth?: string;
  /** When true: no outlined border (default/hover/focus), no outer box padding, minimal field padding (keeps space for the chevron), and a flush menu list / paper. */
  hideBorder?: boolean;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  value,
  onChange,
  options,
  labelKey,
  placeholder,
  minWidth,
  heightOverride,
  fontSizeOverride,
  redOutline = false,
  fullWidth = false,
  useSVGIcons = false,
  pinIndexKey,
  dropDownWidth = "auto",
  hideBorder = false,
}) => {
  const resolvePinIndex = (option: Option): number | null => {
    if (pinIndexKey) {
      const n = Number(option[pinIndexKey]);
      return Number.isFinite(n) ? n : null;
    }
    if (useSVGIcons) {
      const n = Number(option.id);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const renderFeatureValue = (selected: (string | number)[]) => {
    if (!selected.length) return placeholder;

    const selectedOptions = options.filter((opt) => selected.includes(opt.id));

    if (selectedOptions.length >= 2) {
      return "Multiple";
    }
    const opt = selectedOptions[0];
    const label = opt[labelKey];
    const pinIdx = resolvePinIndex(opt);
    const pinUrl = pinIdx != null ? getSimilarAssetsPinIconUrl(pinIdx) : undefined;
    if (!pinUrl) {
      return label;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img
          src={pinUrl}
          alt=""
          width={PIN_IMG_WIDTH}
          height={PIN_IMG_HEIGHT}
          style={{ objectFit: "contain" }}
        />
        <span>{label}</span>
      </Box>
    );
  };

  const handleChange = (event: any) => {
    const selectedValues = event.target.value;
    if (selectedValues.includes("select-all")) {
      if (value.length === options.length) {
        onChange({ target: { value: [] } });
      } else {
        const allIds = options.map((option) => option.id);
        onChange({ target: { value: allIds } });
      }
    } else {
      onChange(event);
    }
  };

  const isAllSelected = value.length === options.length && options.length > 0;

  return (
    <Box
      className="custom-multi-select-box"
      sx={hideBorder ? { p: 0, m: 0 } : undefined}
    >
      <Select
        className="custom-multi-select"
        multiple
        value={value}
        onChange={handleChange}
        displayEmpty
        IconComponent={ChevronDown}
        renderValue={(selected) =>
          renderFeatureValue(selected as (string | number)[])
        }
        MenuProps={{
          MenuListProps: {
            className: "custom-multi-select-menu-list",
            style: {
              width: dropDownWidth,
            },
            ...(hideBorder
              ? {
                  sx: {
                    py: 0,
                    px: 0,
                    "& .MuiMenuItem-root": {
                      padding: "0 !important",
                    },
                  },
                }
              : {}),
          },
          ...(hideBorder
            ? {
                PaperProps: {
                  sx: { p: 0, m: 0 },
                },
              }
            : {}),
        }}
        sx={{
          minWidth: minWidth || "0px",
          width: fullWidth ? "100%" : undefined,
          fontSize: fontSizeOverride || "12px",
          height: heightOverride || "32px",
          color: value.length > 0 ? "#182737 !important" : "#8E8E93 !important",
          ...(hideBorder
            ? {
                "& fieldset": { border: "none" },
                "&:hover:not(.Mui-disabled) fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none !important" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-select": {
                  paddingLeft: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingRight: "28px",
                },
                "& .MuiOutlinedInput-input": {
                  paddingLeft: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingRight: "28px",
                },
              }
            : {
                "& fieldset": {
                  borderColor: redOutline ? "#BA2C2C" : "#C4C4C4",
                  borderWidth: "1px",
                },
                "&:hover:not(.Mui-disabled) fieldset": {
                  borderColor: redOutline ? "#BA2C2C" : "#212121",
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  border: redOutline
                    ? "1px solid #BA2C2C"
                    : "1px solid #245EA0 !important",
                },
              }),
        }}
        inputProps={{ "aria-label": "Without label" }}
      >
        <MenuItem
          value=""
          disabled
          sx={{ fontSize: fontSizeOverride || "12px" }}
        >
          {placeholder}
        </MenuItem>
        <MenuItem
          value="select-all"
          disableRipple
          sx={{ fontSize: fontSizeOverride || "12px" }}
        >
          <Checkbox
            checked={isAllSelected}
            sx={{ color: "#1B7A63 !important" }}
          />
          <ListItemText primary="Select All" />
        </MenuItem>

        {options.map((option) => {
          const pinIdx = resolvePinIndex(option);
          const pinUrl =
            pinIdx != null ? getSimilarAssetsPinIconUrl(pinIdx) : undefined;

          return (
            <MenuItem
              key={option.id}
              value={option.id}
              disableRipple
              sx={{ fontSize: fontSizeOverride || "12px" }}
            >
              <Checkbox
                checked={value.includes(option.id)}
                sx={{ color: "#1B7A63 !important" }}
              />
              {pinUrl ? (
                <img
                  src={pinUrl}
                  alt=""
                  width={PIN_IMG_WIDTH}
                  height={PIN_IMG_HEIGHT}
                  style={{ objectFit: "contain", marginRight: 8 }}
                />
              ) : null}
              <ListItemText primary={option[labelKey]} />
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

export { CustomMultiSelect };
